import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import Layout from "../components/Layout";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost/securecontrol-backend";

const UserDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [actionLoading, setActionLoading] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");
  const [currentProfile, setCurrentProfile] = useState(null);
  const [currentUserId, setCurrentUserId] = useState(null);

  // Récupérer les infos de l'utilisateur connecté
  useEffect(() => {
    const userStr = localStorage.getItem("user");
    if (userStr) {
      try {
        const userData = JSON.parse(userStr);
        setCurrentProfile(String(userData.profile));
        setCurrentUserId(Number(userData.id));
      } catch (e) {
        console.error("Erreur parse user:", e);
      }
    }
  }, []);

  // Charger les détails de l'utilisateur
  const fetchUserDetails = useCallback(async () => {
    if (!id) return;

    setLoading(true);
    setError("");

    try {
      const token = localStorage.getItem("access_token");
      if (!token) {
        setError("Session expirée. Veuillez vous reconnecter.");
        setTimeout(() => navigate("/login"), 2000);
        return;
      }

      const response = await axios.get(`${API_BASE_URL}/users/get.php?id=${id}`, {
        headers: {
          "Authorization": `Bearer ${token}`
        }
      });
      
      if (response.data.success) {
        setUser(response.data.user);
      } else {
        setError(response.data.message || "Utilisateur non trouvé.");
      }
    } catch (err) {
      if (err.response) {
        const { status, data } = err.response;
        
        if (status === 401) {
          setError("Session expirée. Veuillez vous reconnecter.");
          setTimeout(() => navigate("/login"), 2000);
        } else if (status === 403) {
          setError("Vous n'avez pas la permission de voir cet utilisateur.");
        } else if (status === 404) {
          setError("Utilisateur non trouvé.");
        } else {
          setError(data?.message || `Erreur ${status}: Impossible de charger les détails.`);
        }
      } else if (err.request) {
        setError("Serveur inaccessible. Vérifiez votre connexion.");
      } else {
        setError("Erreur: " + err.message);
      }
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    fetchUserDetails();
  }, [fetchUserDetails]);

  // Fonction canEditUser
  const canEditUser = () => {
    if (!user || !currentProfile) return false;
    
    // Ne pas modifier son propre compte
    if (user.id === currentUserId) {
      return false;
    }
    
    // Admin peut modifier tout le monde
    if (String(currentProfile) === "1") {
      return true;
    }
    
    // Gestionnaire peut modifier contrôleur (2) et agent (4)
    if (String(currentProfile) === "3") {
      const userProfileStr = String(user.profile);
      if (userProfileStr === "2" || userProfileStr === "4") {
        return true;
      }
    }
    
    return false;
  };

  // Fonctions d'actions
  const handleChangeStatus = async () => {
    if (!user) return;
    
    if (user.id === currentUserId) {
      alert("Vous ne pouvez pas modifier votre propre statut.");
      return;
    }

    const newStatus = user.statut_user === 1 ? 0 : 1;
    const action = newStatus === 1 ? "activer" : "désactiver";
    
    if (!window.confirm(`Confirmer ${action} l'utilisateur ${user.prenom} ${user.nom} ?`)) {
      return;
    }

    setActionLoading(true);
    setError("");
    setSuccessMsg("");
    
    try {
      const token = localStorage.getItem("access_token");
      const response = await axios.post(
        `${API_BASE_URL}/users/change_status.php`,
        { id: user.id, statut_user: newStatus },
        {
          headers: {
            "Authorization": `Bearer ${token}`,
            "Content-Type": "application/json"
          }
        }
      );

      if (response.data.success) {
        const result = newStatus === 1 ? "activé" : "désactivé";
        setSuccessMsg(`✅ Utilisateur ${result} avec succès.`);
        
        // Recharger les données
        const refreshResponse = await axios.get(`${API_BASE_URL}/users/get.php?id=${id}`, {
          headers: { "Authorization": `Bearer ${token}` }
        });
        
        if (refreshResponse.data.success) {
          setUser(refreshResponse.data.user);
        }
      } else {
        setError(response.data.message || "Échec de l'opération.");
      }
    } catch (err) {
      console.error("Erreur:", err);
      
      if (err.response?.data?.message) {
        // Vérifier si c'est une erreur d'abonnement
        if (err.response.data.code === 'ABONNEMENT_REQUIS') {
          // Message avec bouton de souscription
          setError(
            <div>
              <div style={{ whiteSpace: 'pre-line', marginBottom: '15px' }}>
                {err.response.data.message}
              </div>
              <div className="d-flex gap-2 justify-content-center">
                <a 
                  href="/abonnements/create"
                  className="btn fw-semibold"
                  style={{
                    backgroundColor: '#4caf50',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    padding: '8px 20px',
                    textDecoration: 'none',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = '#45a049'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = '#4caf50'}
                >
                  <i className="bi bi-box-arrow-up-right"></i>
                  Souscrire à un abonnement
                </a>
                <button
                  className="btn fw-semibold"
                  onClick={() => setError("")}
                  style={{
                    backgroundColor: '#f44336',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    padding: '8px 20px'
                  }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = '#d32f2f'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = '#f44336'}
                >
                  Fermer
                </button>
              </div>
            </div>
          );
        } else {
          setError(err.response.data.message);
        }
      } else if (err.response?.data?.error) {
        setError(err.response.data.error);
      } else {
        setError("Erreur technique lors du changement de statut.");
      }
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!user) return;
    
    if (user.id === currentUserId) {
      alert("Vous ne pouvez pas supprimer votre propre compte.");
      return;
    }

    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer ${user.prenom} ${user.nom} ?`)) {
      return;
    }

    setActionLoading(true);
    setError("");
    setSuccessMsg("");
    
    try {
      const token = localStorage.getItem("access_token");
      const response = await axios.post(
        `${API_BASE_URL}/users/delete.php`,
        { id: user.id },
        {
          headers: {
            "Authorization": `Bearer ${token}`,
            "Content-Type": "application/json"
          }
        }
      );

      if (response.data.success) {
        setSuccessMsg("✅ Utilisateur supprimé avec succès.");
        setTimeout(() => navigate("/users"), 2000);
      } else {
        setError(response.data.message || "Échec de la suppression.");
      }
    } catch (err) {
      console.error("Erreur:", err);
      
      if (err.response?.data?.message) {
        // Vérifier si c'est une erreur d'abonnement
        if (err.response.data.code === 'ABONNEMENT_REQUIS') {
          // Message avec bouton de souscription
          setError(
            <div>
              <div style={{ whiteSpace: 'pre-line', marginBottom: '15px' }}>
                {err.response.data.message}
              </div>
              <div className="d-flex gap-2 justify-content-center">
                <a 
                  href="/abonnements/create"
                  className="btn fw-semibold"
                  style={{
                    backgroundColor: '#4caf50',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    padding: '8px 20px',
                    textDecoration: 'none',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = '#45a049'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = '#4caf50'}
                >
                  <i className="bi bi-box-arrow-up-right"></i>
                  Souscrire à un abonnement
                </a>
                <button
                  className="btn fw-semibold"
                  onClick={() => setError("")}
                  style={{
                    backgroundColor: '#f44336',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    padding: '8px 20px'
                  }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = '#d32f2f'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = '#f44336'}
                >
                  Fermer
                </button>
              </div>
            </div>
          );
        } else {
          setError(err.response.data.message);
        }
      } else {
        setError("Erreur technique lors de la suppression.");
      }
    } finally {
      setActionLoading(false);
    }
  };

  // Formater la date
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Obtenir le nom du profil
  const getProfileName = (profileCode) => {
    const profiles = {
      "1": "Super Admin",
      "2": "Contrôleur",
      "3": "Gestionnaire",
      "4": "Agent"
    };
    return profiles[profileCode] || `Profil ${profileCode}`;
  };

  // Composant Tooltip simple
  const Tooltip = ({ children, text, position = "top" }) => (
    <div className="tooltip-container position-relative d-inline-block">
      {children}
      <div className={`tooltip-text position-absolute bg-dark text-white p-2 rounded shadow-sm`}
           style={{
             bottom: position === "top" ? "100%" : "auto",
             top: position === "bottom" ? "100%" : "auto",
             left: "50%",
             transform: "translateX(-50%)",
             whiteSpace: "nowrap",
             zIndex: 1000,
             fontSize: "0.875rem",
             marginBottom: position === "top" ? "5px" : "0",
             marginTop: position === "bottom" ? "5px" : "0",
             display: 'none'
           }}>
        {text}
        <div className="tooltip-arrow position-absolute"
             style={{
               left: "50%",
               transform: "translateX(-50%)",
               [position === "top" ? "top" : "bottom"]: "100%",
               borderWidth: "5px",
               borderStyle: "solid",
               borderColor: position === "top" 
                 ? "#212529 transparent transparent transparent"
                 : "transparent transparent #212529 transparent"
             }}></div>
      </div>
    </div>
  );

  // Obtenir le badge de profil
  const getProfileBadge = (profile) => {
    switch (parseInt(profile)) {
      case 1:
        return <span className="badge" style={{ backgroundColor: '#dc3545', color: 'white', fontSize: '0.75rem', padding: '4px 8px' }}>Super Admin</span>;
      case 2:
        return <span className="badge" style={{ backgroundColor: '#ffc107', color: '#2E2E2E', fontSize: '0.75rem', padding: '4px 8px' }}>Contrôleur</span>;
      case 3:
        return <span className="badge" style={{ backgroundColor: '#0B3C5D', color: 'white', fontSize: '0.75rem', padding: '4px 8px' }}>Gestionnaire</span>;
      case 4:
        return <span className="badge" style={{ backgroundColor: '#4FC3F7', color: '#2E2E2E', fontSize: '0.75rem', padding: '4px 8px' }}>Agent</span>;
      case 5:
        return <span className="badge" style={{ backgroundColor: '#6D28D9', color: 'white', fontSize: '0.75rem', padding: '4px 8px' }}>Poste de contrôle</span>;
      default:
        return <span className="badge" style={{ backgroundColor: '#6c757d', color: 'white', fontSize: '0.75rem', padding: '4px 8px' }}>Inconnu</span>;
    }
  };

  // Obtenir le badge de statut
  const getStatusBadge = (status) => {
    return status === 1 
      ? <span className="badge" style={{ backgroundColor: '#198754', color: 'white', fontSize: '0.75rem', padding: '4px 8px' }}>Actif</span>
      : <span className="badge" style={{ backgroundColor: '#6c757d', color: 'white', fontSize: '0.75rem', padding: '4px 8px' }}>Inactif</span>;
  };

  if (loading) {
    return (
      <Layout>
        <div className="pagetitle">
          <h1 style={{ color: '#0B3C5D', fontWeight: '600' }}>Détails utilisateur</h1>
          <nav>
            <ol className="breadcrumb">
              <li className="breadcrumb-item">
                <Link to="/users" style={{ color: '#1E88C8' }}>Utilisateurs</Link>
              </li>
              <li className="breadcrumb-item active" style={{ color: '#757575' }}>Chargement...</li>
            </ol>
          </nav>
        </div>
        
        <section className="section">
          <div className="row">
            <div className="col-lg-12">
              <div className="card" style={{ 
                borderRadius: '12px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                border: 'none'
              }}>
                <div className="card-body text-center py-5">
                  <div className="spinner-border" role="status" style={{ color: '#0B3C5D' }}>
                    <span className="visually-hidden">Chargement...</span>
                  </div>
                  <p className="mt-3" style={{ color: '#2E2E2E' }}>Chargement des détails...</p>
                </div>
              </div>
            </div>
          </div>
        </section>
      </Layout>
    );
  }

  if (error && !user) {
    return (
      <Layout>
        <div className="pagetitle">
          <h1 style={{ color: '#0B3C5D', fontWeight: '600' }}>Détails utilisateur</h1>
          <nav>
            <ol className="breadcrumb">
              <li className="breadcrumb-item">
                <Link to="/users" style={{ color: '#1E88C8' }}>Utilisateurs</Link>
              </li>
              <li className="breadcrumb-item active" style={{ color: '#f44336' }}>Erreur</li>
            </ol>
          </nav>
        </div>
        
        <section className="section">
          <div className="row">
            <div className="col-lg-12">
              <div className="card" style={{ 
                borderRadius: '12px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                border: 'none'
              }}>
                <div className="card-body">
                  <div className="alert alert-danger alert-dismissible fade show" style={{ 
                    backgroundColor: '#ffebee',
                    borderColor: '#f44336',
                    color: '#c62828',
                    borderRadius: '8px',
                    borderLeft: '4px solid #f44336'
                  }}>
                    <div className="d-flex align-items-center">
                      <i className="bi bi-exclamation-triangle me-2"></i>
                      <div className="flex-grow-1">{error || "Utilisateur non trouvé"}</div>
                    </div>
                  </div>
                  <div className="text-center mt-3">
                    <button 
                      className="btn"
                      onClick={() => navigate("/users")}
                      style={{
                        color: '#0B3C5D',
                        borderColor: '#0B3C5D',
                        backgroundColor: 'transparent',
                        borderRadius: '8px',
                        padding: '10px 24px',
                        fontWeight: '500',
                        transition: 'all 0.3s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.backgroundColor = '#0B3C5D';
                        e.target.style.color = 'white';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.backgroundColor = 'transparent';
                        e.target.style.color = '#0B3C5D';
                      }}
                    >
                      <i className="bi bi-arrow-left me-2"></i>
                      Retour à la liste
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="pagetitle">
        <h1 style={{ color: '#0B3C5D', fontWeight: '600' }}>Détails utilisateur</h1>
        <nav>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <button 
                className="btn btn-link p-0 border-0 bg-transparent text-decoration-none"
                onClick={() => navigate("/dashboard")}
                style={{ color: '#1E88C8', textAlign: 'left' }}
              >
                Dashboard
              </button>
            </li>
            <li className="breadcrumb-item">
              <button 
                className="btn btn-link p-0 border-0 bg-transparent text-decoration-none"
                onClick={() => navigate("/users")}
                style={{ color: '#1E88C8', textAlign: 'left' }}
              >
                Utilisateurs
              </button>
            </li>
            <li className="breadcrumb-item active" style={{ color: '#0B3C5D' }}>
              {user?.prenom} {user?.nom}
            </li>
          </ol>
        </nav>
      </div>

      <section className="section">
        {/* Messages de succès */}
        {successMsg && (
          <div className="alert alert-success alert-dismissible fade show mb-4" style={{ 
            backgroundColor: '#e8f5e9',
            borderColor: '#4caf50',
            color: '#2e7d32',
            borderRadius: '8px',
            borderLeft: '4px solid #4caf50',
            whiteSpace: "pre-line"
          }}>
            <div className="d-flex align-items-center">
              <i className="bi bi-check-circle me-2" style={{ fontSize: '1.2rem' }}></i>
              <div className="flex-grow-1">{successMsg}</div>
              <button 
                type="button" 
                className="btn-close" 
                onClick={() => setSuccessMsg("")}
              ></button>
            </div>
          </div>
        )}

        {/* Messages d'erreur */}
        {error && (
          <div className="alert alert-danger alert-dismissible fade show mb-4" style={{ 
            backgroundColor: '#ffebee',
            borderColor: '#f44336',
            color: '#c62828',
            borderRadius: '8px',
            borderLeft: '4px solid #f44336',
            whiteSpace: "pre-line"
          }}>
            <div className="d-flex align-items-center">
              <i className="bi bi-exclamation-triangle me-2" style={{ fontSize: '1.2rem' }}></i>
              <div className="flex-grow-1">{error}</div>
              <button 
                type="button" 
                className="btn-close" 
                onClick={() => setError("")}
              ></button>
            </div>
          </div>
        )}

        <div className="row">
          <div className="col-lg-4">
            {/* Carte profil + Actions */}
            <div className="card" style={{ 
              borderRadius: '12px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
              border: 'none'
            }}>
              <div className="card-body">
                <div className="text-center mb-4">
                  {/* Avatar */}
                  <div className="mb-3">
                    {user?.avatar_url ? (
                      <img 
                        src={user.avatar_url} 
                        alt={`${user.prenom} ${user.nom}`}
                        className="rounded-circle"
                        style={{ 
                          width: "100px", 
                          height: "100px", 
                          objectFit: "cover",
                          border: '4px solid #F5F7FA'
                        }}
                      />
                    ) : (
                      <div 
                        className="rounded-circle d-inline-flex align-items-center justify-content-center"
                        style={{ 
                          width: "100px", 
                          height: "100px",
                          backgroundColor: '#E3F2FD',
                          color: '#0B3C5D',
                          border: '4px solid #F5F7FA'
                        }}
                      >
                        <span style={{ fontSize: '2rem', fontWeight: '600' }}>
                          {user?.prenom?.charAt(0)}{user?.nom?.charAt(0)}
                        </span>
                      </div>
                    )}
                  </div>
                  
                  <h4 style={{ color: '#0B3C5D', marginBottom: '8px' }}>{user?.prenom} {user?.nom}</h4>
                  <p className="mb-3" style={{ color: '#1E88C8' }}>{user?.email}</p>
                  
                  <div className="d-flex justify-content-center gap-2 mb-3">
                    {user && getStatusBadge(user.statut_user)}
                    {user && getProfileBadge(user.profile)}
                  </div>
                  
                  {user?.matricule && (
                    <div className="mt-3 pt-3 border-top" style={{ borderColor: '#F5F7FA' }}>
                      <p className="mb-2" style={{ color: '#757575', fontSize: '0.9rem' }}>Matricule</p>
                      <div className="fw-bold" style={{ color: '#2E2E2E', fontSize: '1.1rem' }}>{user.matricule}</div>
                    </div>
                  )}
                </div>

                {/* SECTION ACTIONS */}
                <hr className="my-4" style={{ borderColor: '#F5F7FA' }} />
                <h6 style={{ color: '#0B3C5D', fontWeight: '600', marginBottom: '16px' }}>
                  <i className="bi bi-gear me-2" style={{ color: '#1E88C8' }}></i>
                  Actions
                </h6>
                
                <div className="d-flex flex-wrap gap-3 justify-content-center align-items-center mb-4">
                  {/* Bouton MODIFIER */}
                  {canEditUser() && (
                    <Tooltip text="Modifier l'utilisateur">
                      <button 
                        className="btn"
                        onClick={() => navigate(`/users/${user.id}/edit`)}
                        disabled={actionLoading}
                        style={{
                          width: "48px",
                          height: "48px",
                          borderRadius: "12px",
                          padding: "0",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: '#0B3C5D',
                          borderColor: '#0B3C5D',
                          backgroundColor: 'transparent',
                          transition: 'all 0.3s ease'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.backgroundColor = '#0B3C5D';
                          e.target.style.color = 'white';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.backgroundColor = 'transparent';
                          e.target.style.color = '#0B3C5D';
                        }}
                      >
                        {actionLoading ? (
                          <span className="spinner-border spinner-border-sm"></span>
                        ) : (
                          <i className="bi bi-pencil-fill fs-6"></i>
                        )}
                      </button>
                    </Tooltip>
                  )}
                  
                  {/* Bouton ACTIVER/DÉSACTIVER */}
                  {canEditUser() && user.id !== currentUserId && (
                    <Tooltip text={user.statut_user === 1 ? "Désactiver l'utilisateur" : "Activer l'utilisateur"}>
                      <button 
                        className="btn"
                        onClick={handleChangeStatus}
                        disabled={actionLoading}
                        style={{
                          width: "48px",
                          height: "48px",
                          borderRadius: "12px",
                          padding: "0",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: user.statut_user === 1 ? '#ffc107' : '#198754',
                          borderColor: user.statut_user === 1 ? '#ffc107' : '#198754',
                          backgroundColor: 'transparent',
                          transition: 'all 0.3s ease'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.backgroundColor = user.statut_user === 1 ? '#ffc107' : '#198754';
                          e.target.style.color = user.statut_user === 1 ? '#2E2E2E' : 'white';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.backgroundColor = 'transparent';
                          e.target.style.color = user.statut_user === 1 ? '#ffc107' : '#198754';
                        }}
                      >
                        {actionLoading ? (
                          <span className="spinner-border spinner-border-sm"></span>
                        ) : (
                          <i className={`bi ${user.statut_user === 1 ? 'bi-person-x-fill' : 'bi-person-check-fill'} fs-6`}></i>
                        )}
                      </button>
                    </Tooltip>
                  )}
                  
                  {/* Bouton SUPPRIMER */}
                  {canEditUser() && user.id !== currentUserId && user.statut_user === 1 && (
                    <Tooltip text="Supprimer l'utilisateur">
                      <button 
                        className="btn"
                        onClick={handleDelete}
                        disabled={actionLoading}
                        style={{
                          width: "48px",
                          height: "48px",
                          borderRadius: "12px",
                          padding: "0",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: '#dc3545',
                          borderColor: '#dc3545',
                          backgroundColor: 'transparent',
                          transition: 'all 0.3s ease'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.backgroundColor = '#dc3545';
                          e.target.style.color = 'white';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.backgroundColor = 'transparent';
                          e.target.style.color = '#dc3545';
                        }}
                      >
                        {actionLoading ? (
                          <span className="spinner-border spinner-border-sm"></span>
                        ) : (
                          <i className="bi bi-trash-fill fs-6"></i>
                        )}
                      </button>
                    </Tooltip>
                  )}
                </div>

                {/* Info si pas d'actions */}
                {!canEditUser() && (
                  <div className="alert alert-info p-3 text-center mb-4" style={{ 
                    backgroundColor: '#E3F2FD',
                    borderColor: '#4FC3F7',
                    color: '#0B3C5D',
                    borderRadius: '8px'
                  }}>
                    <small>
                      <i className="bi bi-info-circle me-1"></i>
                      {user?.id === currentUserId 
                        ? "Vous ne pouvez pas modifier votre propre compte ici." 
                        : "Vous ne pouvez pas modifier cet utilisateur."}
                    </small>
                  </div>
                )}
                
                {/* Bouton RETOUR */}
                <div className="d-grid">
                  <button 
                    className="btn"
                    onClick={() => navigate("/users")}
                    disabled={actionLoading}
                    style={{
                      color: '#2E2E2E',
                      borderColor: '#e0e0e0',
                      backgroundColor: 'transparent',
                      borderRadius: '8px',
                      padding: '10px 16px',
                      fontWeight: '500',
                      transition: 'all 0.3s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = '#F5F7FA';
                      e.target.style.borderColor = '#0B3C5D';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = 'transparent';
                      e.target.style.borderColor = '#e0e0e0';
                    }}
                  >
                    <i className="bi bi-arrow-left me-2"></i>
                    Retour à la liste
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="col-lg-8">
            {/* Informations détaillées */}
            <div className="card mb-4" style={{ 
              borderRadius: '12px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
              border: 'none'
            }}>
              <div className="card-body">
                <h5 className="card-title" style={{ 
                  color: '#0B3C5D',
                  borderBottom: '2px solid #F5F7FA',
                  paddingBottom: '16px',
                  marginBottom: '24px'
                }}>
                  <i className="bi bi-info-circle me-2" style={{ color: '#1E88C8' }}></i>
                  Informations personnelles
                </h5>

                <div className="row">
                  <div className="col-md-6">
                    <div className="mb-4">
                      <h6 style={{ color: '#0B3C5D', marginBottom: '16px', fontWeight: '600' }}>
                        <i className="bi bi-person-vcard me-2" style={{ color: '#1E88C8' }}></i>
                        Identité
                      </h6>
                      <table className="table table-sm table-borderless">
                        <tbody>
                          <tr>
                            <td width="40%" style={{ color: '#757575', fontWeight: '500', paddingBottom: '12px' }}>Prénom:</td>
                            <td style={{ color: '#2E2E2E', paddingBottom: '12px' }}>{user?.prenom}</td>
                          </tr>
                          <tr>
                            <td style={{ color: '#757575', fontWeight: '500', paddingBottom: '12px' }}>Nom:</td>
                            <td style={{ color: '#2E2E2E', paddingBottom: '12px' }}>{user?.nom}</td>
                          </tr>
                          <tr>
                            <td style={{ color: '#757575', fontWeight: '500', paddingBottom: '12px' }}>Email:</td>
                            <td style={{ color: '#2E2E2E', paddingBottom: '12px' }}>
                              <a href={`mailto:${user?.email}`} className="text-decoration-none" style={{ color: '#1E88C8' }}>
                                {user?.email}
                              </a>
                            </td>
                          </tr>
                          <tr>
                            <td style={{ color: '#757575', fontWeight: '500' }}>Téléphone:</td>
                            <td style={{ color: '#2E2E2E' }}>
                              {user?.phone ? (
                                <a href={`tel:${user.phone}`} className="text-decoration-none" style={{ color: '#2E2E2E' }}>
                                  {user.phone}
                                </a>
                              ) : "-"}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>

                  <div className="col-md-6">
                    <div className="mb-4">
                      <h6 style={{ color: '#0B3C5D', marginBottom: '16px', fontWeight: '600' }}>
                        <i className="bi bi-calendar me-2" style={{ color: '#1E88C8' }}></i>
                        Historique
                      </h6>
                      <table className="table table-sm table-borderless">
                        <tbody>
                          <tr>
                            <td width="40%" style={{ color: '#757575', fontWeight: '500', paddingBottom: '12px' }}>Créé le:</td>
                            <td style={{ color: '#2E2E2E', paddingBottom: '12px' }}>{formatDate(user?.created_at)}</td>
                          </tr>
                          <tr>
                            <td style={{ color: '#757575', fontWeight: '500' }}>Modifié le:</td>
                            <td style={{ color: '#2E2E2E' }}>{formatDate(user?.updated_at) || "-"}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Carte Société */}
            {user?.societe_info && (
              <div className="card" style={{ 
                borderRadius: '12px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                border: 'none'
              }}>
                <div className="card-body">
                  <h5 className="card-title" style={{ 
                    color: '#0B3C5D',
                    borderBottom: '2px solid #F5F7FA',
                    paddingBottom: '16px',
                    marginBottom: '24px'
                  }}>
                    <i className="bi bi-building me-2" style={{ color: '#1E88C8' }}></i>
                    Société
                  </h5>
                  <div className="row">
                    <div className="col-md-6">
                      <div className="mb-3">
                        <table className="table table-sm table-borderless">
                          <tbody>
                            <tr>
                              <td width="40%" style={{ color: '#757575', fontWeight: '500', paddingBottom: '12px' }}>Nom:</td>
                              <td style={{ color: '#2E2E2E', paddingBottom: '12px' }}>{user.societe_info.nom_societe}</td>
                            </tr>
                            <tr>
                              <td style={{ color: '#757575', fontWeight: '500', paddingBottom: '12px' }}>Email:</td>
                              <td style={{ color: '#2E2E2E', paddingBottom: '12px' }}>
                                <a href={`mailto:${user.societe_info.email_societe}`} className="text-decoration-none" style={{ color: '#1E88C8' }}>
                                  {user.societe_info.email_societe}
                                </a>
                              </td>
                            </tr>
                            <tr>
                              <td style={{ color: '#757575', fontWeight: '500' }}>Téléphone:</td>
                              <td style={{ color: '#2E2E2E' }}>
                                {user.societe_info.tel_societe ? (
                                  <a href={`tel:${user.societe_info.tel_societe}`} className="text-decoration-none" style={{ color: '#2E2E2E' }}>
                                    {user.societe_info.tel_societe}
                                  </a>
                                ) : "-"}
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Styles CSS */}
      <style>{`
        .pagetitle h1,
        .card-title,
        .btn,
        .form-label,
        .alert strong,
        h4, h5, h6 {
          font-family: 'Inter', sans-serif;
        }
        
        .card-body,
        .form-control,
        .form-select,
        .form-text,
        .alert,
        .table,
        small,
        p {
          font-family: 'Roboto', sans-serif;
        }
        
        .tooltip-container {
          cursor: pointer;
          position: relative;
          display: inline-block;
        }
        
        .tooltip-container:hover .tooltip-text {
          display: block !important;
        }
        
        .btn:hover {
          transform: translateY(-2px);
          transition: transform 0.2s ease;
        }
        
        .btn:active {
          transform: translateY(0);
        }
        
        .badge {
          border-radius: 6px;
          font-weight: 500;
        }
        
        @media (max-width: 768px) {
          .card {
            margin: 0 -16px;
            border-radius: 0 !important;
          }
          
          .pagetitle h1 {
            font-size: 1.5rem;
          }
          
          .d-flex.flex-wrap {
            justify-content: center !important;
          }
        }
      `}</style>
    </Layout>
  );
};

export default UserDetails;